import { useCallback, useEffect, useRef, useState } from 'react'
import { getYouTubeId, getVimeoId } from '@/utils/media'

const PROGRESS_INTERVAL_MS = 7_000

interface UseExternalVideoProgressOpts {
  /** The original video URL (YouTube/Vimeo link) */
  videoUrl: string | null | undefined
  /** Called every ~7s with current position; also on pause/end */
  onProgress: (watchedSeconds: number, totalDuration: number) => void
  /** If true, stop sending progress (e.g. already completed) */
  disabled?: boolean
}

interface ExternalVideoProgress {
  currentTime: number
  duration: number
  percentage: number
  /** Assign this ref to the <iframe> element */
  iframeRef: React.RefCallback<HTMLIFrameElement>
  /** Seek the external player to a specific time */
  seekTo: (seconds: number) => void
}

/**
 * Tracks progress for YouTube and Vimeo embedded iframes using the
 * postMessage API directly. No external scripts (YT IFrame API / Vimeo
 * SDK) are loaded — this avoids the DOM-replacement problem where
 * `new YT.Player(iframe)` destroys the original element and breaks
 * React refs.
 */
export function useExternalVideoProgress({
  videoUrl,
  onProgress,
  disabled,
}: UseExternalVideoProgressOpts): ExternalVideoProgress {
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const percentage = duration > 0 ? (currentTime / duration) * 100 : 0

  const iframeElRef = useRef<HTMLIFrameElement | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onProgressRef = useRef(onProgress)
  const disabledRef = useRef(disabled)
  const seekTargetRef = useRef<number | null>(null)
  const providerRef = useRef<'youtube' | 'vimeo' | null>(null)
  const latestTimeRef = useRef(0)
  const latestDurationRef = useRef(0)
  const isPlayingRef = useRef(false)
  const ytRetryRef = useRef<ReturnType<typeof setInterval> | null>(null)

  onProgressRef.current = onProgress
  disabledRef.current = disabled

  /* ── helpers ──────────────────────────────────────────────── */

  const clearPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const sendProgress = useCallback(() => {
    if (disabledRef.current) return
    const t = latestTimeRef.current
    const d = latestDurationRef.current
    if (d > 0) onProgressRef.current(t, d)
  }, [])

  const startPolling = useCallback(() => {
    clearPolling()
    intervalRef.current = setInterval(sendProgress, PROGRESS_INTERVAL_MS)
  }, [clearPolling, sendProgress])

  const clearYtRetry = useCallback(() => {
    if (ytRetryRef.current) {
      clearInterval(ytRetryRef.current)
      ytRetryRef.current = null
    }
  }, [])

  /* ── message listener ────────────────────────────────────── */

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeElRef.current
      if (!iframe) return

      // Use origin-based filtering instead of event.source comparison.
      // YouTube embeds use nested internal iframes so event.source may
      // not equal our outer iframe's contentWindow.
      if (providerRef.current === 'youtube' && !event.origin?.includes('youtube.com')) return
      if (providerRef.current === 'vimeo' && !event.origin?.includes('vimeo.com')) return
      if (!providerRef.current) return

      /* ── YouTube ── */
      if (providerRef.current === 'youtube') {
        if (typeof event.data !== 'string') return
        let msg: Record<string, unknown>
        try { msg = JSON.parse(event.data) } catch { return }

        // Any valid message means the handshake worked
        clearYtRetry()

        if (msg.event === 'infoDelivery') {
          const info = msg.info as Record<string, unknown> | undefined
          if (!info) return
          if (typeof info.currentTime === 'number') {
            const t = Math.floor(info.currentTime)
            latestTimeRef.current = t
            setCurrentTime(t)
          }
          if (typeof info.duration === 'number' && (info.duration as number) > 0) {
            const d = Math.floor(info.duration as number)
            latestDurationRef.current = d
            setDuration(d)
          }
          if (typeof info.playerState === 'number') {
            const ps = info.playerState as number
            // 1 = PLAYING, 2 = PAUSED, 0 = ENDED
            if (ps === 1 && !isPlayingRef.current) {
              isPlayingRef.current = true
              startPolling()
            } else if ((ps === 0 || ps === 2) && isPlayingRef.current) {
              isPlayingRef.current = false
              sendProgress()
              clearPolling()
            }
          }
        } else if (msg.event === 'initialDelivery') {
          const info = msg.info as Record<string, unknown> | undefined
          if (info && typeof info.duration === 'number' && (info.duration as number) > 0) {
            const d = Math.floor(info.duration as number)
            latestDurationRef.current = d
            setDuration(d)
          }
          // Apply pending seek
          if (seekTargetRef.current !== null) {
            try {
              iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'seekTo', args: [seekTargetRef.current, true] }),
                '*'
              )
            } catch { /* ignore */ }
            seekTargetRef.current = null
          }
        } else if (msg.event === 'onReady') {
          if (seekTargetRef.current !== null) {
            try {
              iframe.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: 'seekTo', args: [seekTargetRef.current, true] }),
                '*'
              )
            } catch { /* ignore */ }
            seekTargetRef.current = null
          }
        }
        return
      }

      /* ── Vimeo ── */
      if (providerRef.current === 'vimeo') {
        let msg: Record<string, unknown>
        if (typeof event.data === 'string') {
          try { msg = JSON.parse(event.data) } catch { return }
        } else if (typeof event.data === 'object' && event.data !== null) {
          msg = event.data as Record<string, unknown>
        } else {
          return
        }

        const evtName = msg.event as string | undefined
        if (evtName === 'playProgress' || evtName === 'timeupdate') {
          const d = msg.data as Record<string, unknown> | undefined
          if (d) {
            if (typeof d.seconds === 'number') {
              const t = Math.floor(d.seconds)
              latestTimeRef.current = t
              setCurrentTime(t)
            }
            if (typeof d.duration === 'number' && (d.duration as number) > 0) {
              const dur = Math.floor(d.duration as number)
              latestDurationRef.current = dur
              setDuration(dur)
            }
          }
        } else if (evtName === 'play') {
          if (!isPlayingRef.current) {
            isPlayingRef.current = true
            startPolling()
          }
        } else if (evtName === 'pause' || evtName === 'finish' || evtName === 'ended') {
          if (isPlayingRef.current) {
            isPlayingRef.current = false
            sendProgress()
            clearPolling()
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [startPolling, clearPolling, sendProgress, clearYtRetry])

  /* ── ref callback ────────────────────────────────────────── */

  const iframeRef = useCallback(
    (el: HTMLIFrameElement | null) => {
      // Clean up previous
      if (iframeElRef.current && iframeElRef.current !== el) {
        clearPolling()
        clearYtRetry()
        isPlayingRef.current = false
      }
      iframeElRef.current = el
      if (!el || !videoUrl) return

      if (getYouTubeId(videoUrl)) {
        providerRef.current = 'youtube'
        const sendListening = () => {
          try {
            el.contentWindow?.postMessage(JSON.stringify({ event: 'listening' }), '*')
          } catch { /* iframe not ready yet */ }
        }
        // Start the handshake immediately and retry until YouTube responds
        sendListening()
        ytRetryRef.current = setInterval(sendListening, 500)
        // Safety: stop retrying after 10s regardless
        setTimeout(() => clearYtRetry(), 10_000)
      } else if (getVimeoId(videoUrl)) {
        providerRef.current = 'vimeo'
        const initVimeo = () => {
          const events = ['playProgress', 'play', 'pause', 'finish']
          events.forEach(evt => {
            try {
              el.contentWindow?.postMessage(
                JSON.stringify({ method: 'addEventListener', value: evt }),
                '*'
              )
            } catch { /* ignore */ }
          })
          // Apply pending seek
          if (seekTargetRef.current !== null) {
            try {
              el.contentWindow?.postMessage(
                JSON.stringify({ method: 'setCurrentTime', value: seekTargetRef.current }),
                '*'
              )
            } catch { /* ignore */ }
            seekTargetRef.current = null
          }
        }
        // Subscribe after iframe loads; also try after a short delay as fallback
        el.addEventListener('load', initVimeo, { once: true })
        setTimeout(initVimeo, 500)
      }
    },
    [videoUrl, clearPolling, clearYtRetry]
  )

  /* ── seek ─────────────────────────────────────────────────── */

  const seekTo = useCallback((seconds: number) => {
    const iframe = iframeElRef.current
    if (!iframe?.contentWindow) {
      seekTargetRef.current = seconds
      return
    }
    if (providerRef.current === 'youtube') {
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'seekTo', args: [seconds, true] }),
          '*'
        )
      } catch { /* ignore */ }
    } else if (providerRef.current === 'vimeo') {
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'setCurrentTime', value: seconds }),
          '*'
        )
      } catch { /* ignore */ }
    } else {
      seekTargetRef.current = seconds
    }
  }, [])

  /* ── cleanup ──────────────────────────────────────────────── */

  useEffect(() => {
    return () => {
      clearPolling()
      clearYtRetry()
      isPlayingRef.current = false
    }
  }, [clearPolling, clearYtRetry])

  // Reset state when video changes
  useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
    latestTimeRef.current = 0
    latestDurationRef.current = 0
    isPlayingRef.current = false
    providerRef.current = null
  }, [videoUrl])

  return { currentTime, duration, percentage, iframeRef, seekTo }
}
